import type { OptionSheetOption } from '@frontend/api-types';
import { formatCurrencyWithCommasAndDecimal, isNil, type Currency } from '@frontend/duck-tape';
import { applyClass, concatClasses } from '@frontend/web-utils';
import { Card } from '../Primitives/Containers/Card';
import { RenderHTML } from '../Primitives/Containers/RenderHTML';
import { XStack } from '../Primitives/Containers/Stacks/XStack';
import { Icon } from '../Primitives/Icon/Icon';
import { Image } from '../Primitives/Image';
import { Text } from '../Primitives/Texts/Text';

const SELECTED_CHECK_COLOR = '#4ec77a';

export type OptionSheetCardProps = {
  currency: MaybeUndefined<string>;
  disabled: boolean;
  isSelected: boolean;
  onOpenDetails: () => void;
  onToggle: () => void;
  option: OptionSheetOption;
};

export const OptionSheetCard = ({
  currency,
  disabled,
  isSelected,
  onOpenDetails,
  onToggle,
  option,
}: OptionSheetCardProps) => (
  <Card
    className={concatClasses(
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      'w-[320px] min-w-[320px] snap-start overflow-hidden rounded-md bg-white',
      applyClass(isSelected, 'border-2 border-lime-200', 'border-2 border-neutral-200'),
    )}
    padding={0}
  >
    <CardImage source={option.imageUrls?.[0]} />
    <div className="flex flex-1 flex-col gap-y-xs p-md">
      {option.url ? (
        <XStack className="items-start gap-xs">
          <a
            className="underline underline-offset-2 hover:opacity-80"
            href={option.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Text lineClamp={2} type="h5">
              {option.title}
            </Text>
          </a>
          <Icon className="mt-xs shrink-0" color="secondary" name="IconExternalLink" size={14} />
        </XStack>
      ) : (
        <Text lineClamp={2} type="h5">
          {option.title}
        </Text>
      )}
      <RenderHTML className="line-clamp-3 text-p-sm" html={option.description} />
      {option.priceDisplay || !isNil(option.price) ? (
        <Text color="tertiary" type="h6">
          {option.priceDisplay || formatCurrencyWithCommasAndDecimal(option.price, currency as Currency)}
        </Text>
      ) : null}
      <button
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className="mt-auto flex items-center gap-xs self-start pt-sm text-sm text-neutral-400 hover:text-neutral-700"
        onClick={onOpenDetails}
        type="button"
      >
        <span>More details</span>
        <Icon color="tertiary" name="IconChevronRight" size={14} />
      </button>
    </div>
    <ActionBar disabled={disabled} isSelected={isSelected} onToggle={onToggle} />
  </Card>
);

// ─── Internal sub-components ─────────────────────────────────────────────────

const CardImage = ({ source }: { source: MaybeUndefined<string> }) => (
  <div className="relative overflow-hidden bg-neutral-100" style={{ aspectRatio: '16/9' }}>
    {source ? (
      <>
        {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
        <img alt="" className="absolute inset-0 size-full scale-110 object-cover blur-xl" src={source} />
        <img alt="" className="relative size-full object-contain" src={source} />
      </>
    ) : (
      <Image className="size-full" source={source} />
    )}
  </div>
);

type ActionBarProps = {
  disabled: boolean;
  isSelected: boolean;
  onToggle: () => void;
};

const ActionBar = ({ disabled, isSelected, onToggle }: ActionBarProps) => (
  <button
    aria-pressed={isSelected}
    // eslint-disable-next-line tailwindcss/no-custom-classname
    className={concatClasses(
      'flex w-full items-center justify-center gap-sm border-t py-md text-sm font-semibold transition-colors',
      applyClass(
        isSelected,
        'border-lime-200 bg-lime-200 text-neutral-900',
        'border-neutral-200 bg-neutral-100 text-neutral-900 hover:bg-neutral-200',
      ),
      applyClass(disabled, 'pointer-events-none opacity-60', 'cursor-pointer'),
    )}
    disabled={disabled}
    onClick={onToggle}
    type="button"
  >
    <Icon
      color={isSelected ? undefined : 'tertiary'}
      colorOverride={isSelected ? SELECTED_CHECK_COLOR : undefined}
      name={isSelected ? 'IconCircleCheckFilled' : 'IconCircle'}
      size={18}
    />
    Select
  </button>
);
